import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Power, PowerOff, ClipboardList } from 'lucide-react';
import { API_URL } from '../../../utils/apiUrl';

const PROCEDURE_CATEGORIES = [
  'Elektronik', 'Giyim / Tekstil', 'Kozmetik', 'Ev Ürünleri',
  'Aksesuar', 'Kişisel Bakım', 'Gıda', 'Diğer',
];

const emptyForm = { category: '', title: '', description: '', criteria_list: '' };

const TestProceduresManagement = () => {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [showArchived, setShowArchived] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');

  const fetchProcedures = async () => {
    try {
      const url = categoryFilter ? `${API_URL}/api/test-procedures?category=${encodeURIComponent(categoryFilter)}` : `${API_URL}/api/test-procedures`;
      const { data } = await axios.get(url);
      setProcedures(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Prosedürler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProcedures(); }, [categoryFilter]);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.title.trim() || !formData.description.trim()) {
      toast.error('Kategori, başlık ve açıklama zorunludur'); return;
    }
    const criteria = formData.criteria_list.split('\n').map(s => s.trim()).filter(Boolean);
    const payload = {
      category: formData.category,
      title: formData.title.trim(),
      description: formData.description.trim(),
      criteria_list: criteria,
    };
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/test-procedures/${editingId}`, payload);
        toast.success('Prosedür güncellendi (yeni versiyon oluşturuldu)');
      } else {
        await axios.post(`${API_URL}/api/test-procedures`, payload);
        toast.success('Prosedür eklendi');
      }
      setShowForm(false); setEditingId(null); setFormData(emptyForm);
      fetchProcedures();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (proc) => {
    setEditingId(proc.id);
    setFormData({
      category: proc.category,
      title: proc.title,
      description: proc.description,
      criteria_list: (proc.criteria_list || []).join('\n'),
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleActivate = async (id) => {
    try {
      await axios.post(`${API_URL}/api/test-procedures/${id}/activate`);
      toast.success('Prosedür aktif edildi');
      fetchProcedures();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Bu prosedürü devre dışı bırakmak istediğinize emin misiniz?')) return;
    try {
      await axios.post(`${API_URL}/api/test-procedures/${id}/deactivate`);
      toast.success('Prosedür devre dışı bırakıldı');
      fetchProcedures();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  if (loading) return <div className="p-6 text-neutral-500">Yükleniyor...</div>;

  const displayed = showArchived ? procedures : procedures.filter(p => p.active);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black tracking-tight" data-testid="procedures-mgmt-title">Test Prosedürleri</h2>
          <p className="text-sm text-neutral-500 mt-1">Her kategori için merkezi test kriterlerini yönetin. Ürünler bağlı oldukları kategorinin aktif prosedürünü otomatik gösterir.</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditingId(null); setFormData(emptyForm); }}
          className="bg-[#1C3B2A] text-white px-4 py-2 rounded-md text-sm font-bold flex items-center gap-2 hover:bg-[#15302a] transition" data-testid="add-procedure-btn">
          <Plus className="w-4 h-4" /> Yeni Prosedür
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="procedure-filters">
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="procedure-category-filter">
          <option value="">Tüm kategoriler</option>
          {PROCEDURE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived-toggle" />
          <span className="text-neutral-700 font-medium">Arşivlenmişleri göster</span>
        </label>
      </div>

      {showForm && (
        <div className="border border-neutral-200 rounded-md p-6 mb-6 bg-neutral-50">
          <h3 className="text-lg font-bold mb-4">{editingId ? 'Prosedürü Düzenle (yeni versiyon)' : 'Yeni Prosedür Ekle'}</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Kategori *</label>
              <select name="category" value={formData.category} onChange={handleChange} required
                disabled={!!editingId}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm disabled:bg-neutral-100" data-testid="procedure-form-category">
                <option value="">Seçin</option>
                {PROCEDURE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {editingId && <p className="text-[10px] text-neutral-400 mt-1">Düzenleme sırasında kategori değiştirilemez.</p>}
            </div>
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Başlık *</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} required
                placeholder="Örn: Elektronik Test Standardı"
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="procedure-form-title" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold mb-1 text-neutral-600">Açıklama *</label>
              <textarea name="description" value={formData.description} onChange={handleChange} required rows="3"
                placeholder="Bu kategori için uygulanan test prosedürünün genel açıklaması..."
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="procedure-form-description" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold mb-1 text-neutral-600">Test Kriterleri (her satıra bir kriter)</label>
              <textarea name="criteria_list" value={formData.criteria_list} onChange={handleChange} rows="6"
                placeholder={'Pil ömrü\nIsınma testi\nDüşürme testi'}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm font-mono" data-testid="procedure-form-criteria" />
              <p className="text-[10px] text-neutral-400 mt-1">Boş satırlar otomatik temizlenir.</p>
            </div>
            <div className="md:col-span-2 flex gap-3 mt-2">
              <button type="submit" className="bg-[#1C3B2A] text-white px-5 py-2 rounded text-sm font-bold hover:bg-[#15302a] transition" data-testid="procedure-form-submit">
                {editingId ? 'Yeni Versiyonu Kaydet' : 'Kaydet'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setFormData(emptyForm); }}
                className="border border-neutral-300 px-5 py-2 rounded text-sm font-bold hover:bg-neutral-100 transition" data-testid="procedure-form-cancel">
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {displayed.length === 0 ? (
        <div className="border border-dashed border-neutral-300 rounded-md p-10 text-center" data-testid="procedure-empty-list">
          <ClipboardList className="w-8 h-8 text-neutral-300 mx-auto mb-3" />
          <p className="text-sm text-neutral-500">Henüz prosedür bulunmuyor.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="procedure-list">
          {displayed.map(p => (
            <div key={p.id} className={`border rounded-md p-5 ${p.active ? 'border-[#1C3B2A] bg-[#1C3B2A]/5' : 'border-neutral-200 bg-neutral-50'}`} data-testid={`procedure-card-${p.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs font-bold uppercase text-neutral-500">{p.category}</span>
                    <span className="text-xs font-bold text-neutral-400">v{p.version}</span>
                    {p.active ? (
                      <span className="text-[10px] font-black text-white bg-[#1C3B2A] px-2 py-0.5 rounded-full">AKTİF</span>
                    ) : (
                      <span className="text-[10px] font-black text-white bg-neutral-400 px-2 py-0.5 rounded-full">ARŞİV</span>
                    )}
                  </div>
                  <h4 className="text-base font-black text-[#111]">{p.title}</h4>
                  <p className="text-sm text-neutral-600 mt-1">{p.description}</p>
                </div>
                <div className="flex gap-2">
                  {p.active ? (
                    <button onClick={() => handleDeactivate(p.id)}
                      className="text-xs font-bold text-amber-700 hover:underline flex items-center gap-1" data-testid={`deactivate-procedure-${p.id}`}>
                      <PowerOff className="w-3.5 h-3.5" /> Devre Dışı Bırak
                    </button>
                  ) : (
                    <button onClick={() => handleActivate(p.id)}
                      className="text-xs font-bold text-emerald-700 hover:underline flex items-center gap-1" data-testid={`activate-procedure-${p.id}`}>
                      <Power className="w-3.5 h-3.5" /> Aktif Et
                    </button>
                  )}
                  <button onClick={() => handleEdit(p)}
                    className="text-xs font-bold text-[#1C3B2A] hover:underline flex items-center gap-1" data-testid={`edit-procedure-${p.id}`}>
                    <Edit2 className="w-3.5 h-3.5" /> Düzenle
                  </button>
                </div>
              </div>
              {p.criteria_list && p.criteria_list.length > 0 && (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mt-3 ml-1">
                  {p.criteria_list.map((c, i) => (
                    <li key={i} className="text-sm text-neutral-700 flex items-start gap-2">
                      <span className="text-[#1C3B2A] mt-0.5">•</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="text-[10px] text-neutral-400 mt-3">
                Son güncelleme: {p.last_updated_at ? new Date(p.last_updated_at).toLocaleString('tr-TR') : '-'}
                {p.updated_by_name && <span> · {p.updated_by_name}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestProceduresManagement;
